import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSession } from '@/lib/auth-client';

/**
 * Index Route - Smart redirect based on auth state
 *
 * Development (Vite at 5173, Wrangler at 8787):
 * - Authenticated users → /create (SPA)
 * - Unauthenticated users → http://localhost:8787/ (backend landing page)
 *
 * Production (everything at same origin):
 * - This route should never be hit because Hono serves landing page at /
 * - If somehow reached, redirect to /create if auth'd, else reload for SSR
 */
export function IndexPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (isPending || hasRedirected.current) return;

    if (session?.user) {
      // Authenticated - go to app
      hasRedirected.current = true;
      navigate({ to: '/create' });
    } else {
      // Not authenticated - redirect to landing page
      hasRedirected.current = true;

      const isDev = import.meta.env.DEV;
      if (isDev) {
        // Development: landing page is on backend port 8787
        // Pass return origin so login can redirect back here
        window.location.href = `http://localhost:8787/?returnTo=${encodeURIComponent(window.location.origin)}`;
      } else {
        // Production: Hono should serve landing at /, but if we're here
        // it means the SPA intercepted. Do a hard reload to let Hono handle it.
        // Use replace to prevent back button issues
        window.location.replace(window.location.origin);
      }
    }
  }, [session, isPending, navigate]);

  // Empty screen while checking auth - redirect happens quickly
  return <div className="h-screen bg-slate-50" />;
}
